import cx from 'classnames';
import { throttle } from 'lodash';
import { useCallback, useEffect, useRef, useState } from 'react';
import NumberFormat from 'react-number-format';
import { countDecimals, decimals } from '../../../helpers/jsHelper';
import ArrowDownIconV2 from '../../icons/ArrowDownIconV2';
import styles from './NumberInput.module.scss';
import { errorToastV2 } from '../../../helpers/toastHelper';

// Test cases
// 1. min/max
// 2. onlyIntegers
// 3. enableNegative
// 4. step
// 5. normalizeOnBlur
// 6. normalizeOnExeedsLimit
// 7. skipEmpty

export const NumberInput = ({
  onChange,
  value: defaultValue,
  onlyIntegers,
  enableNegative,
  withArrows,
  containerClassName,
  step = 1,
  min,
  max,
  inputProps,
  normalizeOnBlur,
  skipEmpty,
  normalizeOnExeedsLimit,
  onBlur,
  InputComponent = 'input',
  onMouseUp,
  onMouseDown,
  theme,
  version,
  size,
  ...rest
}) => {
  const isV2 = version === '2';
  step = parseFloat(step);
  const decimalsCount = onlyIntegers ? 0 : countDecimals(step);
  const [value, setValue] = useState(String(defaultValue));
  const lastValidNumberRef = useRef(NaN);
  const lastUpdatedNumberRef = useRef(NaN);
  if (isV2 && (min < 0 || max < 0)) enableNegative = true;

  if (!Number.isNaN(parseFloat(defaultValue))) {
    lastValidNumberRef.current = String(defaultValue);
  }

  // This is needed to sync uncontrolled values from parent
  useEffect(() => {
    const value = Number.isNaN(parseFloat(defaultValue)) ? 0 : defaultValue;
    const stringValue = String(value);
    if (lastUpdatedNumberRef.current !== stringValue) {
      lastUpdatedNumberRef.current = stringValue;
      setValue(stringValue);
    }
  }, [defaultValue]);

  const _onChange = (e, ...args) => {
    const stringValue = String(e.target.value);
    lastUpdatedNumberRef.current = stringValue;
    onChange?.(
      {
        ...e,
        target: {
          value: stringValue,
          name: rest.name,
        },
      },
      ...args
    );
  };

  const _setValue = (value) => {
    setValue(String(value));
  };

  const handleChange = (e) => {
    const value = e.target.value;
    const floatValue = parseFloat(value);
    const isNumber = !Number.isNaN(floatValue);

    // Always store what the user typed → keeps UI responsive
    _setValue(value);

    // Don’t notify parent if value is empty/whitespace
    if (isNumber && !String(floatValue)?.trim?.()) return;

    // Invalid numeric string? → don’t call onChange
    if (!isNumber) return;

    // If enforcing min/max during typing
    if (typeof min === 'number' && floatValue < min) {
      isV2 && errorToastV2(`You cannot enter a value less than ${min}`);
      return; // don’t call parent
    }
    if (typeof max === 'number' && floatValue > max) {
      isV2 && errorToastV2(`You cannot enter a value more than ${max}`);
      return; // don’t call parent
    }

    // Always emit string type
    _onChange?.(e);
  };

  const handleBlur = (e) => {
    // Try to parse the value
    let floatValue = parseFloat(value);
    const isNumber = !Number.isNaN(floatValue);

    // Handle empty/skipped values
    if (skipEmpty && isNumber && !String(floatValue)?.trim?.()) {
      let normalizedValue =
        isV2 && typeof skipEmpty === 'function'
          ? skipEmpty(lastValidNumberRef.current)
          : lastValidNumberRef.current;
      if (Number.isNaN(parseFloat(normalizedValue))) {
        normalizedValue = 0;
      }
      _setValue(normalizedValue);

      // this is needed as parent gets old value in onBlur
      e.target.value = String(normalizedValue);

      // Emit to keep values in sync if its changed
      if (lastUpdatedNumberRef.current !== e.target.value) {
        _onChange?.(e);
      }
      onBlur?.(e);
      return;
    }

    // If parseFloat fails or value is just symbols, reset to last valid or 0
    if (Number.isNaN(floatValue)) {
      floatValue = lastValidNumberRef.current || 0;
    }

    // Apply min/max constraints
    if (typeof min === 'number' && floatValue < min) {
      isV2 && errorToastV2(`You cannot enter a value less than ${min}`);
      floatValue = min;
    }
    if (typeof max === 'number' && floatValue > max) {
      isV2 && errorToastV2(`You cannot enter a value more than ${max}`);
      floatValue = max;
    }

    // Normalize: remove trailing zeros, convert -0 to 0
    let normalizedValue = +floatValue;
    if (Object.is(normalizedValue, -0)) normalizedValue = 0;
    _setValue(normalizedValue);

    // this is needed as parent gets old value in onBlur
    e.target.value = String(normalizedValue);

    // Emit to keep values in sync if its changed
    if (lastUpdatedNumberRef.current !== e.target.value) {
      _onChange?.(e);
    }
    onBlur?.(e);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleBlur?.(e);
      e?.target?.blur();
    }
  };

  const { handleMouseDown, dragging } = useDragValueChange({
    value,
    step,
    decimalsCount,
    min,
    max,
    name: rest.name,
    onChange: handleChange,
    onMouseDown,
  });

  const input = (
    <NumberFormat
      {...rest}
      {...inputProps}
      value={value}
      decimalScale={decimalsCount}
      allowNegative={enableNegative}
      isNumericString={true}
      onChange={handleChange}
      onBlur={handleBlur}
      onMouseDown={handleMouseDown}
      onMouseUp={onMouseUp}
      onKeyDown={(e) => handleKeyPress(e)}
      data-testid="number-input-input"
      className={cx(
        styles.input,
        rest.className,
        dragging && styles.dragging,
        isV2 && styles.v2,
        size && styles[`${size}Size`]
      )}
      customInput={InputComponent}
      autoComplete="off"
      autoCorrect="off"
      spellCheck={false}
    />
  );

  if (withArrows) {
    const handleArrowUpClick = () => {
      const fValue = parseFloat(value);
      let newValue = Number.isNaN(parseFloat(fValue)) ? 0 : fValue;
      if (isV2) {
        // when step is less than 0 there might be issues like 1.9 - 0.1 = 1.7999999999999998;
        const dCount = countDecimals(step);
        if (dCount > 0) {
          const multiplier = Math.pow(10, dCount);
          const stepMultiplied = step * multiplier;
          const valueMultiplied = value * multiplier;
          newValue = (valueMultiplied + stepMultiplied) / multiplier;
        } else {
          newValue += step;
        }
        if (typeof max === 'number' && newValue > max) return;
        _setValue(newValue);
        _onChange(
          {
            target: {
              name: rest.name,
              value: newValue,
            },
          },
          { triggeredBy: 'arrowUp' }
        );
      } else {
        newValue += step;
        if (typeof max === 'number' && newValue > max) return;
        _setValue(newValue);
        _onChange(
          {
            target: {
              name: rest.name,
              value: newValue,
            },
          },
          { triggeredBy: 'arrowUp' }
        );
      }
    };

    const handleArrowDownClick = () => {
      const fValue = parseFloat(value);
      let newValue = Number.isNaN(fValue) ? 0 : fValue;
      if (isV2) {
        // when step is less than 0 there might be issues like 1.9 - 0.1 = 1.7999999999999998;
        const dCount = countDecimals(step);
        if (dCount > 0) {
          const multiplier = Math.pow(10, dCount);
          const stepMultiplied = step * multiplier;
          const valueMultiplied = value * multiplier;
          newValue = (valueMultiplied - stepMultiplied) / multiplier;
        } else {
          newValue -= step;
        }
        if (typeof min === 'number' && newValue < min) return;
        _setValue(newValue);
        _onChange(
          {
            target: {
              name: rest.name,
              value: newValue,
            },
          },
          { triggeredBy: 'arrowUp' }
        );
      } else {
        newValue -= step;
        if (typeof min === 'number' && newValue < min) return;
        _setValue(newValue);
        _onChange(
          {
            target: {
              name: rest.name,
              value: newValue,
            },
          },
          { triggeredBy: 'arrowUp' }
        );
      }
    };

    return (
      <div
        className={cx(containerClassName, styles.container, theme === 'dark' && styles.darkTheme)}>
        {input}
        <div className={cx(styles.arrowContainer, isV2 && styles.v2)}>
          <div
            className={styles.arrowUp}
            onClick={handleArrowUpClick}
            onMouseDown={handleMouseDown}
            data-testid="number-input-arrowUp">
            {isV2 ? <ArrowDownIconV2 /> : <Arrow />}
          </div>
          <div
            className={styles.arrowDown}
            onClick={handleArrowDownClick}
            onMouseDown={handleMouseDown}
            data-testid="number-input-arrowDown">
            {isV2 ? <ArrowDownIconV2 /> : <Arrow />}
          </div>
        </div>
      </div>
    );
  }

  return input;
};

const useDragValueChange = ({
  value,
  onChange,
  name,
  step,
  decimalsCount,
  min,
  max,
  onBlur,
  onMouseDown,
}) => {
  const [dragging, setDragging] = useState(false);
  const valueRef = useRef(value);
  const clickDataRef = useRef(null);
  const onChangeRef = useRef(null);
  valueRef.current = value;
  onChangeRef.current = onChange;

  const changeValue = useCallback(
    throttle(
      (posX, initX, startValue) => {
        const s = step || 1;
        let nextValue = decimals(startValue + (posX - initX) * s, decimalsCount);
        if (typeof min === 'number' && nextValue < min) {
          nextValue = min;
        }

        if (typeof min === 'number' && nextValue > max) {
          nextValue = max;
        }

        onChangeRef.current?.({ target: { value: nextValue, name } });
      },
      30,
      { trailing: true }
    ),
    [min, max]
  );

  const handleMouseMove = useCallback((e) => {
    const initX = clickDataRef?.current?.initX;
    if (typeof clickDataRef?.current?.initX !== 'number') return;
    const startValue = parseFloat(clickDataRef.current.value);
    changeValue(e.pageX, initX, startValue);
  });

  const handleMouseDown = useCallback((e) => {
    clickDataRef.current = {
      initX: e.pageX,
      value: valueRef.current,
    };
    document.body.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    onMouseDown?.(e);
    setDragging(true);
    document.body.classList.add('--number-input-drag-change-active');
  });

  const handleMouseUp = useCallback((e) => {
    clickDataRef.current = null;
    document.body.removeEventListener('mousemove', handleMouseMove);
    window.removeEventListener('mouseup', handleMouseUp);
    setDragging(false);
    // increase.cancel();
    // decrease.cancel();
    document.body.classList.remove('--number-input-drag-change-active');
  });

  return {
    handleMouseDown,
    handleMouseUp,
    dragging,
  };
};

const Arrow = () => (
  <svg width="18" height="16" viewBox="0 0 18 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M9.32313 10.3608C8.9191 10.3608 8.51506 10.2113 8.20915 9.91781L4.44585 6.30708C4.27847 6.14648 4.27847 5.88066 4.44585 5.72006C4.61324 5.55946 4.89029 5.55946 5.05768 5.72006L8.82097 9.33079C9.09802 9.59661 9.54823 9.59661 9.82529 9.33079L13.5886 5.72006C13.756 5.55946 14.033 5.55946 14.2004 5.72006C14.3678 5.88066 14.3678 6.14648 14.2004 6.30708L10.4371 9.91781C10.1312 10.2113 9.72716 10.3608 9.32313 10.3608Z"
      fill="currentColor"
    />
  </svg>
);
